import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import errorHandler from "../../../utils/errorHandler";
import { toast } from "react-toastify";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import BTNLoader from "../../../lib/components/BTNLoader";
import * as Yup from "yup";
import userservice from "../../../service/userservice";
import organizationservice from "../../../service/organizationservice";
import AppSelectField, { AppMultiSelect } from "../../../lib/components/AppSelectField";
import NumberFormatCustom from "../../../lib/components/CurrencyFormat";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import getDeviceDetails from "../../../utils/getDeviceDetails";
import _ from "lodash";

const schema = Yup.object({
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  position: Yup.string().required(),
  roleName: Yup.string().required(),
  email: Yup.string().trim().email("Email must be valid").required("Email address is required"),
});

const defaultValues = {
  organizationId: "",
  email: "",
  firstName: "",
  lastName: "",
  roleName: "SUB_ADMIN",
  position: "",
};
const AddNewAdminUser = ({ isOpen, onClose, refetch, organizationId }) => {
  const formHook = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: defaultValues,
  });

  const errors = formHook.formState.errors;
  const control = formHook.control;
  const handleSubmit = formHook.handleSubmit;
  const reset = formHook.reset;

  const mutation = useMutation({
    mutationFn: (data) => organizationservice.addAdministrator(data),
    onError: ({ response }) => errorHandler(response),
    onSuccess: ({ data }) => {
      reset(defaultValues);
      toast.success("User added successfully.");
      refetch();
      onClose();
    },
  });

  async function save(data) {
    data.organizationId = organizationId;
    mutation.mutate(data);
  }

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };
  const { isPending } = mutation;

  return (
    <React.Fragment>
      <Dialog
        open={isOpen}
        maxWidth="sm"
        PaperProps={{
          className: "w-100",
          component: "form",
          onSubmit: handleSubmit(save),
        }}
      >
        <div className="d-flex justify-content-between align-items-center gap-4 p-3 py-2 pe-2">
          <div className="fs-5 fw-medium">New User</div>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>
        <Divider className="bg-secondary-subtle" />
        <DialogContent className="h-100 overflow-auto">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className="mb-4">
                <label className="d-block">
                  First Name
                  <span className="text-danger">*</span>
                </label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="Enter First Name"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      helperText={errors["firstName"]?.message}
                      error={!!errors["firstName"]}
                    />
                  )}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="mb-4">
                <label className="d-block">
                  Last Name
                  <span className="text-danger">*</span>
                </label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="Enter Last Name"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      helperText={errors["lastName"]?.message}
                      error={!!errors["lastName"]}
                    />
                  )}
                />
              </div>
            </Grid>
          </Grid>

          <div className="mb-4">
            <label className="d-block">
              Email
              <span className="text-danger">*</span>
            </label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  type="email"
                  size="small"
                  variant="outlined"
                  placeholder="Enter email address"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  helperText={errors["email"]?.message}
                  error={!!errors["email"]}
                />
              )}
            />
          </div>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <div className="mb-4">
                <label htmlFor="" className="d-block">
                  Position <span className="text-danger">*</span>
                </label>
                <Controller
                  name="position"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      helperText={errors["position"]?.message}
                      error={!!errors["position"]}
                    />
                  )}
                />
              </div>
            </Grid>
            <Grid item xs={12} md={6}>
              <div className="mb-4">
                <label htmlFor="" className="d-block">
                  Role <span className="text-danger">*</span>
                </label>
                <Controller
                  name="roleName"
                  control={control}
                  render={({ field }) => (
                    <AppSelectField
                      value={field.value}
                      placeholder="Select Platform"
                      onChange={(e) => field.onChange(e.target.value)}
                      helperText={errors["roleName"]?.message}
                      error={!!errors["roleName"]}
                      items={["SUB_ADMIN", "ACCOUNTANT"]?.map((item) => ({
                        label: _.capitalize(item?.split("_").join(" ")),
                        value: item,
                      }))}
                    />
                  )}
                />
              </div>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            startIcon={<BTNLoader isLoading={isPending} />}
            variant="contained"
            color={"secondary"}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default AddNewAdminUser;
